import { useEffect, useState } from "react";
import { api } from "@/lib/api-client";

export interface CurrentJobType {
  id: string;
  /** Per-Job-Type verify threshold (R-32 P2). null = inherit 0.95 default. */
  verifyThreshold: number | null;
}

interface JobTypeResponse {
  id: string;
  verificationHighlightThresholds?: { threshold?: number | null } | null;
}

/**
 * Fetches the current Job Type's verify threshold so the verifier store
 * can drive tab-skip + highlight per-JT. Graceful: a null jobTypeId or a
 * failed fetch yields null data, and the shared resolver then falls back
 * to the 0.95 default. Pattern mirrors useCurrentTenant.
 */
export function useCurrentJobType(
  jobTypeId: string | null,
): { data: CurrentJobType | null; loading: boolean } {
  const [data, setData] = useState<CurrentJobType | null>(null);
  const [loading, setLoading] = useState(Boolean(jobTypeId));

  useEffect(() => {
    // R-90 — guard on falsy, not just `=== null`. A load-order race in the
    // verifier transiently yields `undefined` (queued-doc batch/jobType
    // context not yet populated); the old `=== null` check let that through
    // and fired `GET /api/job-types/undefined` → 404.
    if (!jobTypeId) {
      setData(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    // .then(onFulfilled, onRejected) — attach rejection handler
    // synchronously (see useCurrentTenant for the microtask rationale).
    api.get<JobTypeResponse>(`/job-types/${jobTypeId}`).then(
      (jt) => {
        if (cancelled) return;
        setData({
          id: jt.id,
          verifyThreshold: jt.verificationHighlightThresholds?.threshold ?? null,
        });
        setLoading(false);
      },
      () => {
        if (cancelled) return;
        setData(null);
        setLoading(false);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [jobTypeId]);

  return { data, loading };
}
